/**
 * Client component that initializes OpenTelemetry tracing and metrics.
 * 
 * This component should be included in the root layout to enable
 * tracing and metrics across the entire application.
 */

'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { initializeOpenTelemetry, createRouteChangeTransaction } from '@/lib/otel';
import {
  initializeElasticRum,
  startElasticRumPageLoad,
  trackElasticRumRouteChange,
} from '@/lib/elasticRum';

export function OpenTelemetryProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Skip if disabled
      if (process.env.NEXT_PUBLIC_OTEL_DISABLED === 'true') {
        return;
      }

      // Use OpenTelemetry RUM only (Elastic RUM disabled in local compose).
      const otelServiceName = process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME || 'ui';
      const otelServiceVersion = process.env.NEXT_PUBLIC_OTEL_SERVICE_VERSION || '1.0.0';

      const result = initializeOpenTelemetry(otelServiceName, otelServiceVersion);
      if (result.tracerProvider) {
        createRouteChangeTransaction(pathname || '/');
      }

      const pageLoadName = pathname || window.location.pathname || '/';
      initializeElasticRum(pageLoadName);
      startElasticRumPageLoad(pageLoadName);
    }
  }, []);

  // Track route changes for Next.js SPA navigation
  useEffect(() => {
    if (typeof window !== 'undefined' && pathname) {
      // Skip if disabled
      if (process.env.NEXT_PUBLIC_OTEL_DISABLED === 'true') {
        return;
      }

      // OpenTelemetry route-change span
      const timeoutId = setTimeout(() => {
        createRouteChangeTransaction(pathname);
        trackElasticRumRouteChange(pathname);
      }, 100); // Small delay to ensure route is fully loaded

      return () => clearTimeout(timeoutId);
    }
  }, [pathname]);

  return <>{children}</>;
}

